/**
 * WMS Algorithm Engine — shared types
 *
 * A user-configurable decision engine for WMS operations. Instead of
 * hardcoded TypeScript strategies, a stored `AlgorithmDefinitionDoc`
 * describes how a domain's *candidates* are filtered, scored, and selected.
 *
 * Computational model: candidate scoring strategy
 *   generate candidates → filter (hard constraints) → weighted score → select.
 *
 * Sign convention: the engine MINIMIZES the total score — **lower = better** —
 * matching the existing putaway/picking code (`candidates.sort((a,b)=>a.score-b.score)`).
 *
 * These types are shared so the backend engine and the (future) no-code
 * builder UI agree on one schema.
 */

// ════════════════════════════════════════════════════════════════════════
// DOMAINS
// ════════════════════════════════════════════════════════════════════════

/**
 * WMS operations the engine can drive. Only `PUTAWAY` is wired in the first
 * arc; the others are reserved so the enum (and its DB type) is stable as
 * adapters are added.
 */
export enum AlgorithmDomain {
  PUTAWAY = 'PUTAWAY',
  ALLOCATION = 'ALLOCATION',
  PICKING = 'PICKING',
  REPLENISHMENT_SOURCE = 'REPLENISHMENT_SOURCE',
  SORTING_CONSOLIDATION = 'SORTING_CONSOLIDATION',
  PREPACKING = 'PREPACKING',
  /**
   * Demand-side allocation: distribute a SCARCE pool of one product across the
   * competing open order lines that want it. The same engine primitive flipped
   * supply→demand — candidate = a sales-order line, GREEDY_FILL pool = the
   * available quantity (priority-fill; FAIR_SHARE proportional mode lands in E2).
   */
  DEMAND_ALLOCATION = 'DEMAND_ALLOCATION',
  /**
   * Cross-dock match: distribute a just-received quantity of one product across
   * the competing waiting OUTBOUND lines (shipment lines) that need it — so the
   * goods route straight to shipping instead of being put away. The same engine
   * primitive (candidate = a waiting outbound line, GREEDY_FILL pool = received
   * qty); replaces the cross-dock module's hardcoded match scoring.
   */
  CROSS_DOCK = 'CROSS_DOCK',
}

// ════════════════════════════════════════════════════════════════════════
// FIELD CATALOG — the vocabulary a domain adapter exposes
// ════════════════════════════════════════════════════════════════════════

/** Primitive value types a candidate/context field may hold. */
export type AlgorithmFieldType = 'number' | 'boolean' | 'string' | 'enum';

/** Hint to the builder UI on which direction is "better" for a numeric field. */
export type AlgorithmDirectionHint = 'LOWER_BETTER' | 'HIGHER_BETTER' | 'NONE';

/**
 * One field a domain adapter makes available to filters/scoring. Drives both
 * the builder UI (dropdowns) and the compiler's whitelist (injection guard).
 */
export interface AlgorithmFieldDef {
  /** Stable identifier referenced from filters/scoring (e.g. `same_product_qty`). */
  key: string;
  /** Human label for the builder UI. */
  label: string;
  type: AlgorithmFieldType;
  /** For `enum` fields, the allowed values. */
  enumValues?: string[];
  /** Direction hint for numeric scoring fields. */
  directionHint?: AlgorithmDirectionHint;
  /** What the field means — shown as helper text. */
  description?: string;
}

/** A domain's full catalog: candidate fields + context (input) variables. */
export interface AlgorithmCatalog {
  /** Fields present on each candidate (e.g. a location row). */
  candidateFields: AlgorithmFieldDef[];
  /** Variables describing the request (e.g. `neededQty`, `productVelocity`). */
  contextFields: AlgorithmFieldDef[];
}

// ════════════════════════════════════════════════════════════════════════
// DEFINITION LANGUAGE — what a user authors and we persist (jsonb)
// ════════════════════════════════════════════════════════════════════════

export type FilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'nin'
  | 'isTrue'
  | 'isFalse'
  | 'isNull'
  | 'isNotNull';

/**
 * A reference to a context variable, used as a comparison value so a filter
 * can compare a candidate field against a request input
 * (e.g. `available_units >= ctx.neededQty`).
 */
export interface ContextRef {
  ref: string;
}

export function isContextRef(value: unknown): value is ContextRef {
  return (
    typeof value === 'object' && value !== null && typeof (value as ContextRef).ref === 'string'
  );
}

/**
 * A hard constraint. Candidates failing ANY filter are dropped before scoring.
 * Multiple filters are AND-ed.
 */
export interface FilterRule {
  /** Candidate field key from the adapter catalog. */
  field: string;
  operator: FilterOperator;
  /**
   * Comparison value: a literal, an array literal (for `in`/`nin`), or a
   * `ContextRef` to a request variable. Omitted for unary operators
   * (`isTrue`/`isFalse`/`isNull`/`isNotNull`).
   */
  value?: string | number | boolean | Array<string | number> | ContextRef;
  /**
   * ADVANCED escape hatch (later arc): a raw JEXL expression returning a
   * boolean. When set, `field`/`operator`/`value` are ignored.
   */
  expression?: string;
}

export type ScoringDirection = 'LOWER_BETTER' | 'HIGHER_BETTER';

/**
 * One weighted factor in the score. Total score = Σ over factors of
 * `sign(direction) * weight * value`, where a `HIGHER_BETTER` factor is
 * negated so the engine can keep minimizing.
 */
export interface ScoringFactor {
  /** Shown in the dry-run / explainability breakdown. */
  label: string;
  /** Numeric (or boolean → 0/1 bonus) candidate field key. */
  field: string;
  /** Magnitude of this factor's contribution. */
  weight: number;
  direction: ScoringDirection;
  /**
   * Optional gate: the factor only contributes when this condition passes
   * (e.g. a "same lot" bonus that only applies when `has_same_lot` is true).
   */
  condition?: Omit<FilterRule, 'expression'>;
  /**
   * ADVANCED escape hatch (later arc): a raw JEXL expression returning a
   * number. When set, `field`/`direction` are ignored (weight still applies).
   */
  expression?: string;
}

/**
 * How raw factor values are combined into the score:
 * - `NONE` (default): legacy raw weighting — `Σ sign·weight·value`. Weights must
 *   be hand-tuned to each field's scale (distance 0-1000 dwarfs a 0/1 boolean).
 * - `MINMAX`: each factor's value is min-max normalized to [0,1] across the
 *   surviving candidate set BEFORE weighting, so a `weight` is the factor's
 *   maximum contribution and weights become directly comparable across factors.
 */
export type ScoreNormalization = 'NONE' | 'MINMAX';

export type SelectionMode = 'TOP_1' | 'TOP_N' | 'GREEDY_FILL' | 'FAIR_SHARE';

/**
 * A secondary ordering applied only when two candidates' total scores are equal
 * (within a tiny epsilon). Resolved in order — the first tie-breaker that
 * separates two candidates wins. Without any, tied candidates keep the engine's
 * stable input order (candidate-generation order).
 */
export interface TieBreaker {
  /** A numeric candidate field key from the catalog. */
  field: string;
  direction: ScoringDirection;
}

/**
 * Caps how many of the selected candidates may share the same group, so a
 * selection is spread out instead of piling into one place. The group is keyed
 * by a candidate field (e.g. `aisle_code` → "at most N bins per aisle"). The
 * best-ranked candidates in each group are kept.
 */
export interface PerGroupLimit {
  /** Candidate field whose value defines the group. */
  groupField: string;
  /** Maximum selected candidates per distinct group value. */
  max: number;
}

/**
 * How candidates are chosen from the ranked list:
 * - `TOP_1`: best single candidate.
 * - `TOP_N`: best `n` candidates.
 * - `GREEDY_FILL`: walk the ranked list accumulating capacity until the
 *   request's required quantity is met (uses `quantityField` per candidate
 *   and `requiredQtyContextKey` from context).
 */
export interface SelectionPolicy {
  mode: SelectionMode;
  /** Required for `TOP_N`. */
  n?: number;
  /**
   * Quantity-distributing modes (`GREEDY_FILL`, `FAIR_SHARE`): candidate field
   * holding the per-candidate quantity — fillable capacity (supply) or wanted
   * amount (demand).
   */
  quantityField?: string;
  /**
   * Quantity-distributing modes (`GREEDY_FILL`, `FAIR_SHARE`): context key
   * holding the total quantity to distribute — the required amount (supply) or
   * the available pool (demand).
   */
  requiredQtyContextKey?: string;
  /**
   * `GREEDY_FILL` break-level policy. A candidate field holding a pack size
   * (e.g. `units_per_case`, `units_per_pallet`, `units_per_pack`). When set, each
   * candidate's allocation is rounded **down** to a whole multiple of that pack
   * size — so the engine never breaks a pack below this level. Any remainder that
   * can't form a whole pack is left unfilled (it shows up as shortfall). Candidates
   * whose pack-size value is ≤ 1 (loose) are filled normally. Omitted ⇒ no rounding.
   */
  breakLevelField?: string;
  /** Secondary ordering for score ties (applied in order). */
  tieBreakers?: TieBreaker[];
  /**
   * Set-level quality gate. Since **lower = better**, only candidates whose
   * total score is **≤ this value** are eligible for selection; everything
   * worse is dropped. If nothing qualifies the selection is empty — which a
   * flow can route via the `empty` edge (e.g. to a RAISE_EXCEPTION action).
   * Omitted ⇒ no gate (every ranked candidate is eligible).
   */
  scoreThreshold?: number;
  /** Spread the selection: cap how many picks may share a group. */
  perGroupLimit?: PerGroupLimit;
}

/** The full authored definition, persisted in `wms_algorithms.definition`. */
export interface AlgorithmDefinitionDoc {
  filters: FilterRule[];
  scoring: ScoringFactor[];
  selection: SelectionPolicy;
  /**
   * How factor values are combined (default `NONE` = legacy raw weighting).
   * `MINMAX` normalizes each factor across the candidate set so weights are
   * comparable. Omitted ⇒ `NONE` (backward-compatible).
   */
  scoreNormalization?: ScoreNormalization;
}

// ════════════════════════════════════════════════════════════════════════
// EXECUTION — engine input/output (not persisted)
// ════════════════════════════════════════════════════════════════════════

/** A single ranked option produced by a domain adapter. */
export interface AlgorithmCandidate {
  /** Stable id (e.g. location id). */
  id: string;
  /** Display label (e.g. location code). */
  label: string;
  /** Flat field bag keyed by catalog `key`; values are JEXL-safe primitives. */
  fields: Record<string, string | number | boolean | null>;
  /** The adapter's original row, carried through for result mapping. */
  raw: unknown;
}

/** Per-factor contribution, returned for explainability / dry-run. */
export interface ScoreContribution {
  label: string;
  /** The signed amount this factor added to the total. */
  contribution: number;
  /** Whether a gated factor's condition passed (undefined if ungated). */
  applied?: boolean;
}

/** A scored, surviving candidate. */
export interface ScoredCandidate {
  candidate: AlgorithmCandidate;
  score: number;
  breakdown: ScoreContribution[];
  /**
   * For `GREEDY_FILL`: how much of the required quantity THIS candidate covers
   * (its capacity, except the last which takes the remainder). Lets a fanned-out
   * action create one task per bin with the correct per-bin quantity. Undefined
   * for other selection modes.
   */
  allocatedQty?: number;
}

/** Engine result: the selected candidates plus how many were evaluated. */
export interface AlgorithmExecutionResult {
  selected: ScoredCandidate[];
  /** All survivors (post-filter) ranked — superset of `selected`. */
  ranked: ScoredCandidate[];
  evaluatedCount: number;
  filteredOutCount: number;
}

// ════════════════════════════════════════════════════════════════════════
// FLOW LAYER — multi-step decision graphs (chain decisions + branch + act)
// ════════════════════════════════════════════════════════════════════════

/**
 * A flow chains the single-decision engine into a graph:
 * - `DECIDE` runs an algorithm and binds its result,
 * - `BRANCH` routes on a JEXL condition over the flow context,
 * - `ACTION` performs a mutating side-effect (create task, reserve, …),
 * - `START`/`END` bracket the walk.
 */
export type FlowNodeType = 'START' | 'DECIDE' | 'BRANCH' | 'ACTION' | 'SUBFLOW' | 'END';

/** Mutating side-effects a flow can perform (domain-specific, registered). */
export type FlowActionType =
  | 'CREATE_PUTAWAY_TASK'
  | 'RESERVE_LOCATION'
  | 'RAISE_EXCEPTION'
  | 'CROSS_DOCK'
  | 'CREATE_BACKORDER'
  | 'ADD_BONUS_LINE';

/** One configurable parameter of a flow action (drives the builder form). */
export interface ActionParamField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select';
  required?: boolean;
  description?: string;
  options?: Array<{ value: string; label: string }>;
}

/**
 * Self-describing metadata for a registered flow action. The builder reads the
 * action registry (`GET /algorithm-flows/actions`) and renders the param form
 * from `params` — so a new action becomes selectable with zero UI changes.
 */
export interface FlowActionMeta {
  type: FlowActionType;
  label: string;
  description: string;
  params: ActionParamField[];
}

export interface FlowNodePosition {
  x: number;
  y: number;
}

/** DECIDE: run a stored algorithm (id) or an inline definition; bind the result. */
export interface FlowDecideData {
  algorithmId?: string;
  algorithmDefinition?: AlgorithmDefinitionDoc;
  /** Result is stored under `context.results[bindAs]` for later nodes. */
  bindAs: string;
}

/** BRANCH: a JEXL boolean over `context.*` → follow the `true`/`false` edge. */
export interface FlowBranchData {
  condition: string;
}

/** ACTION: a mutating effect fed by a prior decision's selected candidate(s). */
export interface FlowActionData {
  actionType: FlowActionType;
  /** Which bound decision provides the target candidate(s). */
  useResult: string;
  /**
   * Which of the bound decision's selected candidates to act on:
   * - `TOP` (default) — only the top pick (backward-compatible).
   * - `ALL` — fan the action out over every selected candidate (e.g. one
   *   putaway task per bin chosen by a GREEDY_FILL split).
   */
  applyTo?: 'TOP' | 'ALL';
  params?: Record<string, unknown>;
}

/**
 * SUBFLOW: run another saved flow inline (composition / reuse). The sub-flow
 * executes within the same run + transaction; its final decision is bound under
 * `bindAs` so downstream BRANCH/ACTION nodes can reference it like a DECIDE.
 */
export interface FlowSubflowData {
  /** The saved flow (`AlgorithmFlow.id`) to run. */
  flowId: string;
  /** Bind the sub-flow's final decision result under this key. */
  bindAs: string;
}

export type FlowNodeData =
  | FlowDecideData
  | FlowBranchData
  | FlowActionData
  | FlowSubflowData
  | Record<string, never>;

export interface FlowNode {
  id: string;
  type: FlowNodeType;
  position: FlowNodePosition;
  data: FlowNodeData;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  /**
   * `true`/`false` for BRANCH outcomes; `empty` for "previous DECIDE selected
   * nothing" fallback edges; `default` otherwise.
   */
  sourceHandle?: 'true' | 'false' | 'empty' | 'default';
}

export interface FlowGraph {
  nodes: FlowNode[];
  edges: FlowEdge[];
}

/** Per-node execution trace entry (for the builder preview + audit). */
export interface FlowNodeTrace {
  nodeId: string;
  type: FlowNodeType;
  /** Human-readable summary ("Decided: top = A-12", "Branch → true", …). */
  summary: string;
  bind?: string;
}

export interface FlowActionLogEntry {
  actionType: FlowActionType;
  summary: string;
  /** Idempotency reference; present on a live (non-dry-run) action. */
  reference?: string;
  performed: boolean;
}

export interface FlowExecutionResult {
  /** Result of the last DECIDE reached (the flow's effective decision). */
  finalResult: AlgorithmExecutionResult | null;
  trace: FlowNodeTrace[];
  actions: FlowActionLogEntry[];
  dryRun: boolean;
}
